import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Sparkles, Crown, Lock, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Numbers() {
    const navigate = useNavigate();
    const [country, setCountry] = useState("US");
    const [areaCode, setAreaCode] = useState("");
    const [contains, setContains] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buyingId, setBuyingId] = useState(null);
    const [allowance, setAllowance] = useState(null);
    // Vanity
    const [vanityInfo, setVanityInfo] = useState({ price: 4.99, unlocked: false });
    const [vanityResults, setVanityResults] = useState([]);
    const [vanityLoading, setVanityLoading] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    // Plan handoff from Billing: ?plan=lite&cycle=monthly → combined plan+number checkout
    const urlParams = new URLSearchParams(window.location.search);
    const pendingPlan = urlParams.get("plan");
    const pendingCycle = urlParams.get("cycle") === "yearly" ? "yearly" : "monthly";

    const loadAllowance = async () => {
        try {
            const { data } = await api.get("/numbers/allowance");
            setAllowance(data);
        } catch {}
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/numbers/vanity-pricing");
                setVanityInfo(data);
            } catch {}
        })();
        loadAllowance();
    }, []);

    const unlockVanity = async () => {
        setUnlocking(true);
        try {
            const { data } = await api.post("/checkout/session", {
                kind: "vanity",
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start unlock checkout");
            setUnlocking(false);
        }
    };

    const searchVanity = async () => {
        setVanityLoading(true);
        try {
            const params = new URLSearchParams({ country });
            if (contains) params.set("contains", contains);
            const { data } = await api.get(`/numbers/vanity-search?${params.toString()}`);
            setVanityResults(data);
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error("Unlock vanity search first");
            } else {
                toast.error("Vanity search failed");
            }
        } finally {
            setVanityLoading(false);
        }
    };

    const search = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const params = new URLSearchParams({ country });
            if (areaCode) params.set("area_code", areaCode);
            if (contains) params.set("contains", contains);
            const { data } = await api.get(`/numbers/available?${params.toString()}`);
            setResults(data);
        } catch (e) {
            console.error(e);
            toast.error("Search failed");
        } finally {
            setLoading(false);
        }
    };

    const buy = async (n) => {
        if (allowance && !allowance.has_plan) {
            if (pendingPlan) {
                // Combined checkout: subscription + this number in ONE payment
                setBuyingId(n.phone_number);
                try {
                    const { data } = await api.post("/subscriptions/checkout", {
                        plan_id: pendingPlan,
                        billing_cycle: pendingCycle,
                        phone_number: n.phone_number,
                        origin_url: window.location.origin,
                    });
                    window.location.href = data.url;
                } catch (e) {
                    const detail = e?.response?.data?.detail || "Could not start checkout";
                    toast.error(detail);
                    if (e?.response?.status === 412) {
                        window.location.href = "/profile?next=billing";
                    }
                    setBuyingId(null);
                }
                return;
            }
            // No plan chosen yet: carry the chosen number into the plan checkout
            toast.info(`${n.phone_number} selected — choose a plan and your number comes included.`);
            navigate(`/billing?number=${encodeURIComponent(n.phone_number)}`);
            return;
        }
        // Subscriber claiming the number included with their plan — no extra charge
        if (allowance?.included_available) {
            setBuyingId(n.phone_number);
            try {
                await api.post("/numbers/claim-included", { phone_number: n.phone_number });
                toast.success(`${n.phone_number} is yours — included with your ${allowance.plan} plan!`);
                await loadAllowance();
                setResults((prev) => prev.filter((r) => r.phone_number !== n.phone_number));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not claim number");
            } finally {
                setBuyingId(null);
            }
            return;
        }
        setBuyingId(n.phone_number);
        try {
            const { data } = await api.post("/checkout/session", {
                kind: "number",
                phone_number: n.phone_number,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setBuyingId(null);
        }
    };

    return (
        <div className="space-y-8" data-testid="numbers-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Marketplace</p>
                <h1 className="font-display text-4xl font-bold tracking-tight">Get a virtual number</h1>
                <p className="text-zinc-600 mt-2 max-w-2xl">
                    Search Twilio&apos;s global catalog. Pick one and we will wire it to your inbox automatically.
                </p>
                <p className="mt-3 text-sm font-medium text-[#D97706]" data-testid="subscription-required-note">
                    {allowance?.has_plan
                        ? "Your first number is included with your subscription. Extra numbers are $4.99 each, one-time."
                        : "Your phone number is included with your subscription — choose a plan to get started."}
                </p>
            </div>

            {allowance && !allowance.has_plan && pendingPlan && (
                <div className="border border-[#D97706] bg-[#FEF3C7] rounded-sm p-5" data-testid="pending-plan-banner">
                    <p className="font-medium text-[#D97706] capitalize">
                        {pendingPlan} plan selected — now pick your phone number
                    </p>
                    <p className="text-sm text-zinc-600 mt-1">
                        Search below and tap a number. It&apos;s <span className="font-medium">included with your plan</span> —
                        you&apos;ll only be charged the {pendingPlan} {pendingCycle} subscription price.
                    </p>
                </div>
            )}

            {allowance && !allowance.has_plan && !pendingPlan && (
                <div className="border border-[#C4B5FD] bg-[#F5F0FF] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="no-plan-banner">
                    <div>
                        <p className="font-medium text-espresso">A subscription is required to get a number</p>
                        <p className="text-sm text-zinc-600 mt-1">
                            Pick a number below — it&apos;s included with your plan. You only pay the subscription price.
                        </p>
                    </div>
                    <Button
                        onClick={() => navigate("/billing")}
                        data-testid="no-plan-subscribe-btn"
                        className="bg-espresso text-[#FAF5FF] hover:bg-zinc-800 rounded-sm h-10 shrink-0"
                    >
                        See plans <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            )}

            {allowance?.has_plan && (
                <div className="border border-[#DDD6FE] bg-paper rounded-sm p-5 flex items-center gap-4" data-testid="allowance-banner">
                    <Gift className="w-5 h-5 text-emerald-deep shrink-0" />
                    <div className="text-sm">
                        {allowance.included_available ? (
                            <>
                                <span className="font-medium capitalize">{allowance.plan}</span> plan — your{" "}
                                <span className="font-medium text-emerald-deep">included number is ready to claim</span>.
                                Pick any number below at no extra charge.
                            </>
                        ) : (
                            <>
                                <span className="font-medium capitalize">{allowance.plan}</span> plan —{" "}
                                {allowance.numbers_owned} number{allowance.numbers_owned > 1 ? "s" : ""} on your account.
                                Extra numbers cost <span className="font-medium">${allowance.price.toFixed(2)}</span> one-time.
                            </>
                        )}
                    </div>
                </div>
            )}

            <form onSubmit={search} className="border border-zinc-200 rounded-sm p-6 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-2">
                        <label className="font-mono text-[10px] uppercase text-zinc-500">Country</label>
                        <Input
                            value={country}
                            onChange={(e) => setCountry(e.target.value.toUpperCase())}
                            maxLength={2}
                            className="rounded-sm font-mono mt-1"
                            data-testid="search-country-input"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <label className="font-mono text-[10px] uppercase text-zinc-500">Area code</label>
                        <Input
                            value={areaCode}
                            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="415"
                            className="rounded-sm font-mono mt-1"
                            data-testid="search-area-code-input"
                        />
                    </div>
                    <div className="md:col-span-5">
                        <label className="font-mono text-[10px] uppercase text-zinc-500">Contains digits</label>
                        <Input
                            value={contains}
                            onChange={(e) => setContains(e.target.value)}
                            placeholder="optional, e.g. 777"
                            className="rounded-sm font-mono mt-1"
                            data-testid="search-contains-input"
                        />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="search-numbers-btn"
                            className="w-full bg-black text-white hover:bg-zinc-800 rounded-sm h-10"
                        >
                            <Search className="w-4 h-4 mr-2" /> {loading ? "Searching…" : "Search"}
                        </Button>
                    </div>
                </div>
            </form>

            {results.length === 0 && !loading && (
                <div className="border border-zinc-200 rounded-sm p-12 text-center bg-zinc-50/50">
                    <Sparkles className="w-6 h-6 mx-auto text-accent-blue mb-3" />
                    <p className="text-zinc-600">Search Twilio&apos;s catalog above to see available numbers.</p>
                </div>
            )}

            {results.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                    {results.map((n) => (
                        <div key={n.phone_number} className="bg-paper p-6 hover:bg-[#F5F0FF] transition-colors" data-testid={`number-card-${n.phone_number}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-mono text-lg font-medium tracking-tight">{n.phone_number}</div>
                                    {(n.locality || n.region) && (
                                        <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {n.locality}{n.region ? `, ${n.region}` : ""}
                                        </div>
                                    )}
                                </div>
                                {n.demo && (
                                    <span className="font-mono text-[9px] bg-[#E4DAF7] text-[#4C1D95] px-2 py-0.5 rounded-sm uppercase">
                                        Demo
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-4">
                                {n.capabilities.map((c) => (
                                    <span key={c} className="font-mono text-[10px] px-1.5 py-0.5 border border-[#DDD6FE] rounded-sm uppercase">
                                        {c}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-6 flex items-center justify-between">
                                <div>
                                    {allowance && (!allowance.has_plan || allowance.included_available) ? (
                                        <>
                                            <div className="font-display text-2xl font-light text-emerald-deep">Included</div>
                                            <div className="font-mono text-[10px] text-zinc-500 uppercase">with your plan</div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="font-display text-2xl font-light">${n.price.toFixed(2)}</div>
                                            <div className="font-mono text-[10px] text-zinc-500 uppercase">buy extra number · one-time</div>
                                        </>
                                    )}
                                </div>
                                <Button
                                    onClick={() => buy(n)}
                                    disabled={buyingId === n.phone_number}
                                    data-testid={`buy-number-btn-${n.phone_number}`}
                                    className="bg-emerald-deep hover:bg-blue-600 text-[#FAF5FF] rounded-sm h-9"
                                >
                                    {buyingId === n.phone_number
                                        ? "…"
                                        : allowance && !allowance.has_plan
                                          ? pendingPlan ? "Select & checkout" : "Select number"
                                          : allowance?.included_available
                                            ? "Claim my number"
                                            : "Buy extra number"}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Vanity Search Premium */}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-hidden" data-testid="vanity-section">
                <div className="grid lg:grid-cols-12">
                    <div className="lg:col-span-5 p-8 bg-espresso text-[#FAF5FF] relative">
                        <div className="absolute top-4 right-4 flex items-center gap-1 text-[#C4B5FD]">
                            <Crown className="w-3.5 h-3.5" />
                            <span className="font-mono text-[10px] uppercase tracking-wider">Premium</span>
                        </div>
                        <p className="font-mono text-[11px] uppercase tracking-wider text-[#C4B5FD] mb-4">— Vanity search</p>
                    <h3 className="font-display text-3xl font-light leading-tight tracking-tight">
                        Numbers people
                        <br />
                        <span className="font-serif-italic">remember.</span>
                    </h3>
                        <p className="text-sm text-zinc-300 mt-4 leading-relaxed">
                            Find numbers ending in <span className="font-mono text-[#C4B5FD]">777</span>,{" "}
                            <span className="font-mono text-[#C4B5FD]">8888</span>, or containing your favourite digits.
                            One-time unlock — yours forever.
                        </p>
                        {!vanityInfo.unlocked && (
                            <div className="mt-8">
                                <div className="font-display text-4xl font-light">${vanityInfo.price.toFixed(2)}</div>
                                <div className="font-mono text-[10px] text-zinc-400 uppercase mt-1">one-time unlock</div>
                                <Button
                                    onClick={unlockVanity}
                                    disabled={unlocking}
                                    data-testid="vanity-unlock-btn"
                                    className="mt-6 w-full bg-[#FAF5FF] text-espresso hover:bg-white rounded-sm h-11"
                                >
                                    {unlocking ? "Opening…" : "Unlock with Stripe →"}
                                </Button>
                            </div>
                        )}
                        {vanityInfo.unlocked && (
                            <div className="mt-8 px-3 py-2 bg-[#D97706] rounded-sm text-sm text-[#FAF5FF] inline-flex items-center gap-2" data-testid="vanity-unlocked-badge">
                                <Crown className="w-4 h-4 text-[#C4B5FD]" /> Unlocked — search away
                            </div>
                        )}
                    </div>
                    <div className="lg:col-span-7 p-8">
                        <div className="flex items-end justify-between mb-6">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Search vanity catalog</p>
                                <p className="text-sm text-zinc-700 mt-1">
                                    Ranked by memorability score.
                                </p>
                            </div>
                            <Button
                                onClick={searchVanity}
                                disabled={!vanityInfo.unlocked || vanityLoading}
                                data-testid="vanity-search-btn"
                                className="bg-espresso text-[#FAF5FF] rounded-sm h-10"
                            >
                                {vanityInfo.unlocked ? (vanityLoading ? "Searching…" : "Find vanity") : (<><Lock className="w-3.5 h-3.5 mr-2" /> Locked</>)}
                            </Button>
                        </div>
                        {vanityResults.length === 0 && (
                            <div className="border border-dashed border-[#DDD6FE] rounded-sm p-8 text-center text-sm text-zinc-500">
                                {vanityInfo.unlocked
                                    ? "Click Find vanity to see top-scored numbers."
                                    : "Unlock to search high-memorability numbers."}
                            </div>
                        )}
                        {vanityResults.length > 0 && (
                            <ul className="divide-y divide-[#DDD6FE]/60">
                                {vanityResults.slice(0, 8).map((n) => (
                                    <li key={n.phone_number} className="py-3 flex items-center justify-between gap-3" data-testid={`vanity-row-${n.phone_number}`}>
                                        <div className="min-w-0">
                                            <div className="font-mono text-base font-medium">{n.phone_number}</div>
                                            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                                {n.locality}{n.region ? `, ${n.region}` : ""} ·{" "}
                                                <span className="text-emerald-deep">score {n.score}</span>
                                                {n.reasons?.length > 0 && (
                                                    <> · <span className="text-zinc-600">{n.reasons.join(", ")}</span></>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => buy(n)}
                                            disabled={buyingId === n.phone_number}
                                            data-testid={`vanity-buy-${n.phone_number}`}
                                            className="bg-emerald-deep text-[#FAF5FF] rounded-sm h-9 px-4"
                                        >
                                            {buyingId === n.phone_number ? "…" : <>Claim <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
